const BASE = "/api/reconcile";

async function apiFetch<T>(
  url: string,
  options: RequestInit = {},
  token: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = await res.json();
      msg = body.message || body.error || msg;
    } catch {}
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface ReconciliationSession {
  id: string;
  userId: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: "pending" | "running" | "done";
  summary: ReconciliationSummary | null;
  createdAt: string;
}

export interface ReconciliationSummary {
  matched: number;
  unmatched: number;
  discrepancies: number;
  matchedAmount: number;
  unmatchedAmount: number;
  discrepancyAmount: number;
  gatewaySummary: Record<
    string,
    { totalReceived: number; fees: number; openingBalance: number; closingBalance: number }
  >;
}

export interface ReconciliationTransaction {
  id: string;
  sessionId: string;
  source: string;
  externalId: string | null;
  orderId: string | null;
  reference: string | null;
  amount: string;
  currency: string;
  transactionDate: string;
  gateway: string;
  description: string | null;
  matchStatus: "matched" | "unmatched" | "discrepancy" | "dismissed";
  rawData: object | null;
  createdAt: string;
  linkedTxnId: string | null;
  matchType: string | null;
  amountDiff: string | null;
}

export interface GatewayAccountMapping {
  id: string;
  userId: string;
  gateway: string;
  accountName: string;
  createdAt: string;
}

// Sessions
export const listSessions = (token: string | null) =>
  apiFetch<ReconciliationSession[]>(`${BASE}`, {}, token);

export const createSession = (
  token: string | null,
  data: { name: string; periodStart: string; periodEnd: string },
) =>
  apiFetch<ReconciliationSession>(`${BASE}`, { method: "POST", body: JSON.stringify(data) }, token);

export const getSession = (token: string | null, id: string) =>
  apiFetch<ReconciliationSession>(`${BASE}/${id}`, {}, token);

export const deleteSession = (token: string | null, id: string) =>
  apiFetch<void>(`${BASE}/${id}`, { method: "DELETE" }, token);

// Import
export const importTransactions = (
  token: string | null,
  sessionId: string,
  transactions: Array<{
    source: string;
    externalId?: string;
    orderId?: string;
    reference?: string;
    amount: string;
    currency?: string;
    transactionDate: string;
    gateway: string;
    description?: string;
    rawData?: object;
  }>,
) =>
  apiFetch<{ imported: number }>(
    `${BASE}/${sessionId}/import`,
    { method: "POST", body: JSON.stringify({ transactions }) },
    token,
  );

// Run
export const runReconciliation = (token: string | null, sessionId: string) =>
  apiFetch<ReconciliationSession>(`${BASE}/${sessionId}/run`, { method: "POST" }, token);

// Transactions
export const listTransactions = (token: string | null, sessionId: string) =>
  apiFetch<ReconciliationTransaction[]>(`${BASE}/${sessionId}/transactions`, {}, token);

// Patch (link/dismiss/unlink)
export const patchTransaction = (
  token: string | null,
  sessionId: string,
  txnId: string,
  payload: { action: "link" | "dismiss" | "unlink"; linkedTxnId?: string },
) =>
  apiFetch<{ ok: boolean }>(
    `${BASE}/${sessionId}/transactions/${txnId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
    token,
  );

// Gateway mappings
export const listGatewayMappings = (token: string | null) =>
  apiFetch<GatewayAccountMapping[]>(`${BASE}/gateway-mappings`, {}, token);

export const upsertGatewayMapping = (
  token: string | null,
  data: { gateway: string; accountName: string },
) =>
  apiFetch<GatewayAccountMapping>(
    `${BASE}/gateway-mappings`,
    { method: "PUT", body: JSON.stringify(data) },
    token,
  );

// Exports (returns URL to navigate to)
export const exportTransactionsCsvUrl = (sessionId: string) =>
  `${BASE}/${sessionId}/export/transactions`;

export const exportReckonCsvUrl = (sessionId: string) =>
  `${BASE}/${sessionId}/export/reckon`;
